/* Types, Constants, Utils */
import * as Constants from '../../constants';
import {TQueryItem} from '../../constants';
import SearchUtils from "../../utils/search-utils";

import {TFilterOption} from "../../types/search";

import {ISearchDate, ISearchState, UpdateSearchAction} from "../../types/redux/search";
import {createSlice, PayloadAction} from "@reduxjs/toolkit";

const reset = (state: ISearchState): void => {
  state.query = [];
  state.recent_query = false;
  state.dates = [];
  state.current_page = 1;
  state.sort = SearchUtils.sortDefault(window.location.search);
  _resetFilters(state);
  state.suspendSearch = true;
  state.available_filter_options = [];
}

const resetSaved = (state: ISearchState): void => {
  state.query = [Constants.MY_LIBRARY_EMPTY_SEARCH];
  state.dates = [];
  state.current_page = 1;
  state.sort = Constants.MY_LIBRARY_DEFAULT_SORT;
  _resetFilters(state);
  state.recent_query = false;
  state.suspendSearch = false;

  /* clear url param */
  const searchParams = new URLSearchParams(window.location.search);

  searchParams.set(Constants.SORT_KEY, state.sort);
  searchParams.set(Constants.RESULTS_PER_PAGE_KEY, String(state.results_per_page ? state.results_per_page : Constants.DEFAULT_RESULT_PER_PAGE));
  window.history.replaceState(null, '', window.location.pathname + "?" + searchParams.toString());
}

const updateFilterParams = (state: ISearchState): void =>{
  const searchParams = new URLSearchParams(window.location.search);
  if (state.db_set) { // not for content page --> db_set = null
    const filterIds: string[] = state.selected_filters_ids;
    if (filterIds.length > 0) {
      searchParams.set(Constants.FILTERS_KEY, filterIds.join(','));
    } else {
      searchParams.delete(Constants.FILTERS_KEY)
    }
    if (window.location.pathname !== '/' + Constants.HOME + '/') {
      searchParams.set(Constants.SORT_KEY, state.sort);
      window.history.replaceState(null, '', window.location.pathname + '?' + searchParams.toString())
    }
  } else {
   window.history.replaceState(null, '', window.location.pathname);
  }
}

const _resetFilters = (state:ISearchState): void => {
  state.applied_filters = [];
  state.selected_filters_ids = state.applied_filters.map(fo=>fo.id);
  state.dates = [];
  //updateFilterParams(state);
}

const _applySelectedFilters = (state:ISearchState) => {
  /* reset except for advance search filters  */

  state.applied_filters = []
  let filterOptionsSplit: any = {
    dates: [],
    other: []
  };

  state.selected_filters_ids.forEach(id=>{
    const filterOption: TFilterOption = state.available_filter_options.find(fo=>fo.id === id);
    if (filterOption) {
      if ((filterOption.fq_group === Constants.DATE_FACET) || (filterOption.fq_group === Constants.DATE)) {
        filterOptionsSplit.dates.push({...filterOption});
      } else  {
          filterOptionsSplit.other.push({...filterOption});
      }
    } else {
      const displayFilterOption: TFilterOption | undefined = state.filter_display?.options.find(fo=>fo.id === id)
      if (displayFilterOption) {
        filterOptionsSplit.other.push({...displayFilterOption});
      }
    }
  })

  if (filterOptionsSplit.dates.length > 0) {
    /* note: sate.dates may have already been set from searchState initial state but the filter options
    * have not been loaded. In this case filter options not need for valid query since it reference searchStage.dates */
    state.dates = filterOptionsSplit.dates.map((fo:TFilterOption) => {
      const startDate: Date = new Date(fo.value);
      const yearsRange: number = (fo.id.indexOf(Constants.SELECT_ALL) !== -1) ? 10 : 1;
      const endDate: Date = new Date(startDate.getFullYear() + yearsRange, startDate.getMonth(), startDate.getDate() )
      return {
        id: fo.id,
        field: fo.field,
        from: startDate.toLocaleDateString(),
        to: endDate.toLocaleDateString()
      }
    })
  }

  if (filterOptionsSplit.other.length > 0) {
    _addFilters(state, filterOptionsSplit.other)
  }
  updateFilterParams(state);
}

const _addFilters = (state: ISearchState, filtersToAdd: TFilterOption[]) =>{
  let index: number;
  filtersToAdd.forEach(filterOption => {
    index = state.applied_filters.findIndex(f=>(f.id === filterOption.id));
    if (index === -1) {
      if (filterOption.add === Constants.PREPEND) {
        state.applied_filters.unshift({...filterOption}) // some filters must proceed others eg. duplicates must come first
      } else {
        state.applied_filters.push({...filterOption})
      }
    }
  })
}

export const searchSlice = createSlice({
  name: UpdateSearchAction,
  initialState: SearchUtils.fromDataSource(window.location.search),
  reducers: {
    setInitialSearch: (state: ISearchState, action: PayloadAction<boolean>) => {
      state.initialSearch = action.payload
    },

    setSearch: (state: ISearchState, action: PayloadAction<ISearchState>) => {
      return {...action.payload}
    },

    setIndustry: (state: ISearchState, action: PayloadAction<string>) => {
      state.industry = action.payload
    },

    setDBSet: (state: ISearchState, action: PayloadAction<string | null>) => {
      state.db_set = action.payload;
      if (!action.payload) {
        _resetFilters(state)
      }
    },

    /* recent_query --> save query in recent queries */
    setQuery: (state: ISearchState, action: PayloadAction<{query:TQueryItem[], recent_query:boolean}>) => {
      state.query = action.payload.query;
      _resetFilters(state);
      state.recent_query = action.payload.recent_query;
    },

    clearQuery: (state: ISearchState) => {
      state.query = [] ;
      state.recent_query = false;
    },

    setQueryOfIds: (state: ISearchState, action: PayloadAction<string[]>) => {
      let queryItem: TQueryItem = {...Constants.DEFAULT_SEARCH};

      let query: TQueryItem[] = [];
      if (action.payload.length === 0) {
        queryItem.field = Constants.ID;
        queryItem.term = null;
        query.push(queryItem);
      } else {
        for (let i=0; i<action.payload.length; i++) {
          queryItem = {...Constants.DEFAULT_SEARCH};
          queryItem.field = Constants.ID;
          queryItem.term = action.payload[i];
          if (i > 0) {
            queryItem.operator = Constants.OR;
          }
          query.push(queryItem);
        }
      }
      state.query = [...query];
    },

    setResultsPerPage: (state: ISearchState, action: PayloadAction<number>) => {
      state.results_per_page = action.payload
    },

    setCurrentPage: (state: ISearchState, action: PayloadAction<number>) => {
      state.current_page = action.payload
    },

    setSort: (state: ISearchState, action: PayloadAction<string | null>) => {
      state.sort = action.payload;
    },

    setSearchDates: (state: ISearchState, action: PayloadAction<ISearchDate[]>) => {
      state.dates = action.payload;
    },

    removeDate: (state: ISearchState, action: PayloadAction<string>) => {
      state.dates = state.dates.filter(d => d.id !== action.payload);
    },

    /* Filter key = {Constants.DOCUMENTS,Constants.BIBLIOGRAPHY, Constants.TAGS } */
    setFiltersKey: (state: ISearchState, action: PayloadAction<string>) => {
      state.filters_key = action.payload;
    },

    setFiltersLoaded: (state: ISearchState, action: PayloadAction<boolean>) => {
      state.filtersLoaded = action.payload;
    },

    setAvailableFilterOptions: (state: ISearchState, action: PayloadAction<TFilterOption[]>) => {
      state.available_filter_options = action.payload;
    },

    setAppliedFilters: (state: ISearchState, action: PayloadAction<TFilterOption[]>) => {
      state.applied_filters = action.payload;
      updateFilterParams(state);
    },

    applySelectedFilters: (state: ISearchState) => {
      _applySelectedFilters(state)
    },

    addFilters: (state: ISearchState, action: PayloadAction<TFilterOption[]>) => {
      _addFilters(state, action.payload)
      updateFilterParams(state);
    },

    removeFilters: (state: ISearchState, action: PayloadAction<TFilterOption[]>) => {
      let index: number;
      action.payload.forEach(filterOption => {
        index = state.applied_filters.findIndex(f=>(f.id === filterOption.id));
        if (index !== -1) {
          state.applied_filters.splice(index, 1)
        }

        index = state.selected_filters_ids.findIndex(id=>(id === filterOption.id));
        if (index !== -1) {
          state.selected_filters_ids.splice(index, 1)
        }
      })
      updateFilterParams(state);
      state.current_page = 1;
    },

    resetFilters: (state: ISearchState) => {
      _resetFilters(state);
    },

    clearFilters: (state: ISearchState) => {
      state.selected_filters_ids = [];
      state.applied_filters = [];
    },

    setSelectedFilters: (state: ISearchState, action: PayloadAction<string[]>) => {
      state.selected_filters_ids = action.payload;
    },

    addRemoveSelectedFilters: (state: ISearchState, action: PayloadAction<{ids:string[], method:string}>) => {
      const {
        ids
      } = action.payload;

      ids.forEach(id =>{
        const index: number = state.selected_filters_ids.findIndex(selectedId=>(selectedId === id));
        if (action.payload.method === Constants.ADD) {
          if (index === -1) {
            state.selected_filters_ids.push(id)
          }
        } else {
          if (index !== -1) {
            state.selected_filters_ids.splice(index, 1)
          }
        }
      })
    },

    clearSelectedFiltersIds: (state: ISearchState) => {
      state.selected_filters_ids = [];
    },

    searchReset: (state: ISearchState) => {
      reset(state);
    },

    searchResetSaved: (state: ISearchState) => {
      resetSaved(state);
    },

    suspendSearch: (state: ISearchState, action: PayloadAction<boolean>) => {
      state.suspendSearch = action.payload
    },
  }
});

export const {
  setInitialSearch,
  setSearch,
  setIndustry,
  setDBSet,
  setQuery,
  setQueryOfIds,
  clearQuery,
  setResultsPerPage,
  setCurrentPage,
  setSort,
  setSearchDates,
  removeDate,
  setAppliedFilters,
  setFiltersLoaded,
  setFiltersKey,
  addFilters,
  applySelectedFilters,
  removeFilters,
  setAvailableFilterOptions,
  clearFilters,
  resetFilters,
  searchReset,
  searchResetSaved,
  setSelectedFilters,
  addRemoveSelectedFilters,
  clearSelectedFiltersIds,
  suspendSearch
} = searchSlice.actions;
export default searchSlice.reducer;
